import { ResponsiveModal } from "@components/responsive-modal";
import { ChevronDownIcon, DropdownMenu } from "@radix-ui/themes";
import { ReactNode, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export type DropdownItem =
  | {
      type: "item";
      label: ReactNode;
      onClick?: () => void;
      color?: "red" | "default";
      disabled?: boolean;
    }
  | {
      type: "separator";
    }
  | {
      type: "label";
      content: ReactNode;
      className?: string;
    }
  | {
      type: "checkbox";
      label: string;
      checked: boolean;
      onCheckedChange: (checked: boolean) => void;
    }
  | {
      type: "sub";
      label: string;
      items: DropdownItem[];
    };

interface ResponsiveDropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  title?: string;
  className?: string;
}

export const ResponsiveDropdown = ({
  trigger,
  items,
  title = "Menu",
  className = "",
}: ResponsiveDropdownProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState<string | null>(null);

  // Check if device is mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const renderMobileItem = (item: DropdownItem, index: number) => {
    if (item.type === "separator") {
      return (
        <div
          key={`sep-${index}`}
          className="h-px bg-gray-200 dark:bg-gray-700 my-2"
        />
      );
    }

    if (item.type === "label") {
      return (
        <div key={`label-${index}`} className="px-3 py-2">
          {item.content}
        </div>
      );
    }

    if (item.type === "checkbox") {
      return (
        <div
          key={`checkbox-${index}`}
          className="flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => item.onCheckedChange(!item.checked)}
        >
          <span>{item.label}</span>
          <div
            className={twMerge(
              "w-5 h-5 rounded border flex items-center justify-center",
              item.checked
                ? "bg-indigo-600 border-indigo-600"
                : "border-gray-300 dark:border-gray-600"
            )}
          >
            {item.checked && (
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
        </div>
      );
    }

    if (item.type === "sub") {
      const isSubOpen = subMenuOpen === item.label;
      return (
        <div key={`sub-${index}`}>
          <div
            className="flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setSubMenuOpen(isSubOpen ? null : item.label)}
          >
            <span>{item.label}</span>
            <ChevronDownIcon
              className={twMerge(
                "w-4 h-4 transition-transform",
                isSubOpen && "rotate-180"
              )}
            />
          </div>
          {isSubOpen && (
            <div className="ml-4 mt-1">
              {item.items.map((subItem, subIndex) =>
                renderMobileItem(subItem, subIndex)
              )}
            </div>
          )}
        </div>
      );
    }

    // Regular item
    return (
      <div
        key={`item-${index}`}
        className={twMerge(
          "px-3 py-2 rounded-lg",
          item.disabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
          item.color === "red" && "text-red-600 dark:text-red-400"
        )}
        onClick={() => {
          if (!item.disabled && item.onClick) {
            item.onClick();
            setIsOpen(false);
          }
        }}
      >
        {item.label}
      </div>
    );
  };

  const renderDesktopItem = (item: DropdownItem, index: number) => {
    if (item.type === "separator") {
      return <DropdownMenu.Separator key={`sep-${index}`} />;
    }

    if (item.type === "label") {
      return (
        <DropdownMenu.Label key={`label-${index}`} className={item.className}>
          {item.content}
        </DropdownMenu.Label>
      );
    }

    if (item.type === "checkbox") {
      return (
        <DropdownMenu.CheckboxItem
          key={`checkbox-${index}`}
          checked={item.checked}
          onCheckedChange={item.onCheckedChange}
        >
          {item.label}
        </DropdownMenu.CheckboxItem>
      );
    }

    if (item.type === "sub") {
      return (
        <DropdownMenu.Sub key={`sub-${index}`}>
          <DropdownMenu.SubTrigger>{item.label}</DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent sideOffset={5}>
            {item.items.map((subItem, subIndex) =>
              renderDesktopItem(subItem, subIndex)
            )}
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
      );
    }

    // Regular item
    return (
      <DropdownMenu.Item
        key={`item-${index}`}
        onClick={item.onClick}
        color={item.color === "red" ? "red" : undefined}
        disabled={item.disabled}
      >
        {item.label}
      </DropdownMenu.Item>
    );
  };

  // For mobile view - render a bottom sheet
  if (isMobile) {
    return (
      <>
        <div
          className={twMerge("cursor-pointer", className)}
          onClick={() => setIsOpen(true)}
        >
          {trigger}
        </div>

        <ResponsiveModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setSubMenuOpen(null);
          }}
          title={title}
          height="auto"
          maxHeight="80vh"
        >
          <div className="flex flex-col gap-1 pb-4 -m-4">
            {items.map((item, index) => renderMobileItem(item, index))}
          </div>
        </ResponsiveModal>
      </>
    );
  }

  // For desktop view - render Radix DropdownMenu
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={className}>
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        {items.map((item, index) => renderDesktopItem(item, index))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
